import type { RfqWorkspaceProps } from "./rfq-workspace-types";
import { useRfqDrawerEffects } from "./useRfqDrawerEffects";
import { useRef, useState } from "react";
import { localDateTimeToUtc } from "../../rfq-timezone";
import { rfqUiMessage, rfqDeadlinePreview, rfqReviewDeadline } from "./rfq-presentation";
import { useRfqInvitationCommands } from "./useRfqInvitationCommands";
import { useRfqWorkspaceData } from "./useRfqWorkspaceData";
import { useRfqGovernanceCommands } from "./useRfqGovernanceCommands";
import { useRfqDraftCommands } from "./useRfqDraftCommands";

export function useRfqWorkspace({
  requestNo,
  title,
  category,
  quantity,
  unit,
  neededAt,
  user,
  notify: onNotify,
}: RfqWorkspaceProps) {
  const {
    scope, rfq, candidates, selected, loading, error, setRfq, setCandidates, setSelected, setError, load: reloadRfq,
    invitations, invitationLoading, invitationsLoaded, invitationError, setInvitationError, loadInvitations,
    offers, evaluation, contractCompliance, communications, governanceError, setGovernanceError,
    setEvaluation, setContractCompliance, loadGovernance, canManageInvitations,
  } = useRfqWorkspaceData({ requestNo, user, notify: onNotify });

  const {
    form, composeError, submitting, rfqCommandBusy, setForm, setComposeError,
    create, saveSelection, command,
  } = useRfqDraftCommands({
    scope, snapshot: { requestNo, title, category, quantity, unit, neededAt },
    user, rfq, selected, loading, error, setRfq, setCandidates, setError,
  });

  const {
    governanceBusy, deadlineExtension, setDeadlineExtension, unsealReason, setUnsealReason,
    announcementBody, setAnnouncementBody, answerDrafts, setAnswerDrafts,
    extendDeadline, unseal, evaluateUnifiedOffers, evaluateContract, createContractOrder, publishAnnouncement, answerQuestion,
  } = useRfqGovernanceCommands({ scope, user, rfq, offers, communications, setRfq, setEvaluation, setContractCompliance, setGovernanceError, loadGovernance });

  const [composeOpen, setComposeOpen] = useState(false);

  const [composeStep, setComposeStep] = useState<1 | 2>(1);

  const [invitationDrawerSupplierId, setInvitationDrawerSupplierId] = useState<
    string | null
  >(null);

  const [invitationConfirm, setInvitationConfirm] = useState<
    "resend" | "revoke" | null
  >(null);

  const workspaceRef = useRef<HTMLElement>(null);

  const drawerRef = useRef<HTMLElement>(null);

  const composeDrawerRef = useRef<HTMLElement>(null);

  const composeTriggerRef = useRef<HTMLButtonElement>(null);

  const confirmationCancelRef = useRef<HTMLButtonElement>(null);

  const confirmationTriggerRef = useRef<HTMLButtonElement | null>(null);

  const confirmationActionRef = useRef<"resend" | "revoke" | null>(null);

  const closeInvitationConfirmation = () => {
    const action = confirmationActionRef.current;
    setInvitationConfirm(null);
    window.setTimeout(() => {
      const trigger = confirmationTriggerRef.current;
      if (trigger?.isConnected) trigger.focus();
      else {
        const label =
          action === "resend" ? "Yeniden gönder" : "Daveti iptal et";
        const replacement = Array.from(
          drawerRef.current?.querySelectorAll<HTMLButtonElement>("button") ??
            [],
        ).find((button) => button.textContent?.trim() === label);
        if (replacement) replacement.focus();
        else
          drawerRef.current
            ?.querySelector<HTMLElement>(
              '[aria-label="Davet yönetimini kapat"]',
            )
            ?.focus();
      }
    }, 0);
  };

  const {
    invitationMessage, testPortalUrl, invitationPending, testDeliveryLoading,
    invite, invitationCommand, loadTestPortalUrl, clearInvitationFeedback,
  } = useRfqInvitationCommands({
    scope, rfq, canManageInvitations, preview: user.preview === true,
    supplierId: invitationDrawerSupplierId, invitations, invitationsLoaded, loadInvitations,
    setInvitationError, onCommandSettled: closeInvitationConfirmation,
  });

  const closeInvitationDrawer = () => {
    const supplierId = invitationDrawerSupplierId;
    setInvitationDrawerSupplierId(null);
    setInvitationConfirm(null);
    clearInvitationFeedback();
    window.setTimeout(
      () =>
        Array.from(
          workspaceRef.current?.querySelectorAll<HTMLButtonElement>(
            "[data-invitation-supplier]",
          ) ?? [],
        )
          .find((button) => button.dataset.invitationSupplier === supplierId)
          ?.focus(),
      0,
    );
  };

  const closeComposeDrawer = () => {
    setComposeOpen(false);
    setComposeError("");
    setComposeStep(1);
    window.setTimeout(() => composeTriggerRef.current?.focus(), 0);
  };

  const moveComposeStep = (next: 1 | 2) => {
    setComposeError("");
    if (next > composeStep && composeStep === 1) {
      try {
        localDateTimeToUtc(form.deadlineAt, form.timezone);
      } catch (value) {
        setComposeError(
          rfqUiMessage(
            value,
            "Geçerli bir teklif son tarihi ve saat dilimi girin.",
          ),
        );
        window.setTimeout(
          () =>
            composeDrawerRef.current
              ?.querySelector<HTMLInputElement>('input[type="datetime-local"]')
              ?.focus(),
          0,
        );
        return;
      }
    }
    setComposeStep(next);
    window.setTimeout(() => {
      const drawer = composeDrawerRef.current;
      const body = drawer?.querySelector<HTMLElement>(
        ".rfq-create-drawer-body",
      );
      if (body) body.scrollTop = 0;
      drawer
        ?.querySelector<HTMLElement>("[data-step-initial]")
        ?.focus({ preventScroll: true });
    }, 0);
  };

  const deadlinePreview = rfqDeadlinePreview(form);

  const reviewDeadline = rfqReviewDeadline(form);

  const invitationSelection = rfq?.selections?.find(
    (selection) =>
      (selection.supplier_id ?? selection.supplierId) ===
      invitationDrawerSupplierId,
  );

  const drawerInvitation = invitations.find(
    (item) =>
      (item.supplierId ?? item.supplier_id) === invitationDrawerSupplierId,
  );

  const requestDisplayNo = (rfq?.rfq_no ?? rfq?.rfqNo ?? "").replace(
    /RFQ-?/g,
    "",
  );

  const neededAtLabel = /^\d{4}-\d{2}-\d{2}$/.test(neededAt)
    ? new Intl.DateTimeFormat("tr-TR", { dateStyle: "medium" }).format(
        new Date(`${neededAt}T12:00:00Z`),
      )
    : neededAt;

  useRfqDrawerEffects({
    rfq,
    composeOpen,
    invitationDrawerSupplierId,
    invitationConfirm,
    workspaceRef,
    drawerRef,
    composeDrawerRef,
    confirmationCancelRef,
    closeInvitationConfirmation,
    closeInvitationDrawer,
    closeComposeDrawer,
  });

  return {
    requestNo,
    title,
    category,
    quantity,
    unit,
    user,
    rfq,
    candidates,
    selected,
    loading,
    error,
    setSelected,
    reloadRfq,
    invitations,
    invitationLoading,
    invitationsLoaded,
    invitationError,
    loadInvitations,
    offers,
    evaluation,
    contractCompliance,
    communications,
    governanceError,
    loadGovernance,
    canManageInvitations,
    form,
    composeError,
    submitting,
    rfqCommandBusy,
    setForm,
    create,
    saveSelection,
    command,
    governanceBusy,
    deadlineExtension,
    setDeadlineExtension,
    unsealReason,
    setUnsealReason,
    announcementBody,
    setAnnouncementBody,
    answerDrafts,
    setAnswerDrafts,
    extendDeadline,
    unseal,
    evaluateUnifiedOffers,
    evaluateContract,
    createContractOrder,
    publishAnnouncement,
    answerQuestion,
    composeOpen,
    setComposeOpen,
    composeStep,
    invitationDrawerSupplierId,
    setInvitationDrawerSupplierId,
    invitationConfirm,
    setInvitationConfirm,
    workspaceRef,
    drawerRef,
    composeDrawerRef,
    composeTriggerRef,
    confirmationCancelRef,
    confirmationTriggerRef,
    confirmationActionRef,
    closeInvitationConfirmation,
    invitationMessage,
    testPortalUrl,
    invitationPending,
    testDeliveryLoading,
    invite,
    invitationCommand,
    loadTestPortalUrl,
    clearInvitationFeedback,
    closeInvitationDrawer,
    closeComposeDrawer,
    moveComposeStep,
    deadlinePreview,
    reviewDeadline,
    invitationSelection,
    drawerInvitation,
    requestDisplayNo,
    neededAtLabel,
  };
}
